import React, { useState } from 'react'
import { CreditApplication, PersonaInquiryStatus } from '_gql'

import useDidMount from '@divvy-web/hooks.usedidmount'
import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage, useIntl } from '@divvy-web/i18n'
import { BannerType } from '@divvy-web/skylab.banner'
import BasicButton, {
  BASIC_BUTTON_COLOR_ACTION,
  BASIC_BUTTON_SIZE_SMALL,
  BASIC_BUTTON_TYPE_OUTLINED,
} from '@divvy-web/skylab.basicbutton'
import { TOAST_TYPE_DANGER, useToast } from '@divvy-web/skylab.toast'

import { useAuth } from '@sputnik/auth'
import { useTrackCollaboratorEvent } from '@sputnik/hooks/useApplicationCollaborators'
import StatusBanner from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/ApplicationDetailsTabContent/ActionsNeededSection/StatusBanner'
import { matchEmail } from '@sputnik/utils/dataUtils'
import { logError } from '@sputnik/utils/loggerUtils'

import { useGetPersonasLazyQuery } from './GetPersonas.gql'
import PersonaClient from './PersonaClient'
import { useRestartPersonaInquiry } from './RestartPersonaInquiry.gql'
import styles from './styles.module.scss'

interface ConfirmIdentityBannerProps {
  salesforceCreditId: string
  personaInquiries: CreditApplication['personaInquiries']
}

const ConfirmIdentityBanner = ({ personaInquiries = [], salesforceCreditId }: ConfirmIdentityBannerProps) => {
  const [getClassName] = useNameFormatter('ConfirmIdentityBanner')
  const { formatMessage } = useIntl()
  const showToast = useToast()
  const { email } = useAuth()

  const { trackCollaboratorEvent } = useTrackCollaboratorEvent({
    salesforceCreditId,
  })

  useDidMount(() => {
    trackCollaboratorEvent(
      'se_signup__application_dashboard_action_needed_confirm_your_identity__presented',
      (role) => ({
        person_type: role,
        is_auth_signer: role === 'authorizedSigner',
      }),
    )
  })

  const [inquiryId, setInquiryId] = useState<string | null>(null)
  const personaInquiriesToComplete =
    personaInquiries?.filter((inquiry) => !inquiry.status?.includes(PersonaInquiryStatus.COMPLETED)) || []
  const [getPersonas, { loading: isLoadingPersonaStatuses }] = useGetPersonasLazyQuery({
    fetchPolicy: 'network-only',
  })

  const [restartPersonaInquiry, { loading: isResartingPersonaInquiry }] = useRestartPersonaInquiry({
    onCompleted: async (data) => {
      await getPersonas()
      setInquiryId(data?.restartPersonaInquiry?.inquiryId || null)
    },
    onError: (error) => {
      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Could not start verification due to an error. Please try again later.'
          id='sputnik.ConfirmIdentityBanner__7C7iKR'
        />,
        { className: 'dd-privacy-allow' },
      )
      logError(error, {
        attributes: {
          action: 'onConfirmIdentityClick',
          creditAppId: salesforceCreditId,
          message: error?.message,
          result: 'Error restarting persona inquiry',
        },
        eventName: 'RestartPersonaInquiry',
      })
    },
  })

  const onConfirmIdentityClick = (inquiry) => {
    if (inquiry?.status !== PersonaInquiryStatus.CREATED) {
      restartPersonaInquiry({ variables: { inquiryId: inquiry?.inquiryId } })
    } else {
      // upon setting the inquiryId, the PersonaClient component will render and handle the rest of the flow
      setInquiryId(inquiry?.inquiryId || null)
    }
  }

  return (
    <div className={`${styles[getClassName('')]} dd-privacy-allow`}>
      {personaInquiriesToComplete.map((inquiry, index) => {
        const isLoggedInUser = matchEmail(inquiry?.person?.emailAddress, email)

        return (
          <div key={index}>
            <StatusBanner
              key={index}
              bannerType={BannerType.Warning}
              subtext={
                <>
                  <FormattedMessage
                    defaultMessage='{isLoggedInUser, select, 
                      true {Identity confirmation is needed.}
                      other {Identity confirmation is needed for {name}.}
                    }'
                    id='sputnik.ConfirmIdentityBanner__1JRfgv'
                    values={{
                      name: `${inquiry?.person?.firstName} ${inquiry?.person?.lastName}`,
                      isLoggedInUser,
                    }}
                  />

                  {isLoggedInUser && (
                    <BasicButton
                      color={BASIC_BUTTON_COLOR_ACTION}
                      disabled={isResartingPersonaInquiry || isLoadingPersonaStatuses}
                      name={formatMessage(
                        {
                          defaultMessage: 'Confirm {name}',
                          id: 'sputnik.ConfirmIdentityBanner__EIGHzZ',
                        },
                        { name: `${inquiry?.person?.firstName} ${inquiry?.person?.lastName}` },
                      )}
                      size={BASIC_BUTTON_SIZE_SMALL}
                      type={BASIC_BUTTON_TYPE_OUTLINED}
                      onClick={() => {
                        trackCollaboratorEvent(
                          'se_signup__application_dashboard_action_needed_confirm_your_identity__fired',
                          (role) => ({
                            person_type: role,
                          }),
                        )
                        onConfirmIdentityClick(inquiry)
                      }}
                    >
                      <FormattedMessage
                        defaultMessage='Confirm identity'
                        id='sputnik.ConfirmIdentityBanner__aVgLMW'
                      />
                    </BasicButton>
                  )}
                </>
              }
              title={
                <FormattedMessage
                  defaultMessage='Confirmation needed'
                  id='sputnik.ConfirmIdentityBanner__dezVS5'
                />
              }
            />
            {inquiryId && (
              <PersonaClient
                inquiryId={inquiryId}
                setInquiryId={setInquiryId}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

export default ConfirmIdentityBanner
